import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient.js";
import "../../styles/MasterFiling.css";

const DiamondLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("");

  // NEW STATE FOR LOT SUMMARY
  const [lotSummary, setLotSummary] = useState([]);
  const [receiveTotals, setReceiveTotals] = useState({ big: 0, micro: 0 });

  useEffect(() => {
    fetchWorkers();
    fetchLedger();
    fetchLotReceiveSummary();
  }, []);

  useEffect(() => {
    fetchLedger();
    fetchLotReceiveSummary();
  }, [selectedWorker]);

  // LOAD WORKERS
  const fetchWorkers = async () => {
    const { data } = await supabase
      .from("WORKERS")
      .select("id, worker_name")
      .eq("status", true)
      .order("worker_name");

    setWorkers(data || []);
  };

  // LOAD OLD LEDGER (ISSUE AND RECEIVE TABLE)
  const fetchLedger = async () => {
    let query = supabase
      .from("DIAMOND_TRANSACTIONS")
      .select("*, WORKERS(worker_name)")
      .order("created_at", { ascending: false });

    if (selectedWorker) {
      query = query.eq("worker_id", selectedWorker);
    }

    const { data } = await query;
    setLedger(data || []);
  };

  // LOAD RECEIVE STONES PER LOT (NEW SECTION)
  const fetchLotReceiveSummary = async () => {
    if (!selectedWorker) {
      setLotSummary([]);
      setReceiveTotals({ big: 0, micro: 0 });
      return;
    }

    // 1️⃣ Fetch lots of this worker
    const { data: lots } = await supabase
      .from("WAX_LOTS")
      .select("id, lot_no")
      .eq("worker_id", selectedWorker);

    if (!lots || lots.length === 0) {
      setLotSummary([]);
      setReceiveTotals({ big: 0, micro: 0 });
      return;
    }

    let summary = [];
    let totalBig = 0;
    let totalMicro = 0;

    // 2️⃣ For each lot, fetch stones
    for (let lot of lots) {
      const { data: stones } = await supabase
        .from("WAX_RECEIVE_ITEMS")
        .select("big_stone, micro_stone, created_at")
        .eq("lot_id", lot.id);

      if (!stones || stones.length === 0) continue;

      const lotBig = stones.reduce((s, r) => s + Number(r.big_stone || 0), 0);
      const lotMicro = stones.reduce((s, r) => s + Number(r.micro_stone || 0), 0);

      const latestDate = stones
        .map((s) => new Date(s.created_at))
        .sort((a, b) => b - a)[0]
        .toISOString()
        .split("T")[0];

      summary.push({
        date: latestDate,
        lot_no: lot.lot_no,
        big: lotBig,
        micro: lotMicro,
      });

      totalBig += lotBig;
      totalMicro += lotMicro;
    }

    summary.sort((a, b) => new Date(b.date) - new Date(a.date));

    setLotSummary(summary);
    setReceiveTotals({ big: totalBig, micro: totalMicro });
  };

  // OLD TOTALS
  const totalIssued = ledger
    .filter((l) => l.type === "ISSUE")
    .reduce((sum, r) => sum + Number(r.cts), 0);

  const totalReceived = ledger
    .filter((l) => l.type === "RECEIVE")
    .reduce((sum, r) => sum + Number(r.cts), 0);

  const balance = totalIssued - totalReceived;

  return (
    <div className="master-page">
      <div className="ongoing-card">
        <h2 className="ongoing-title">📘 Diamond Ledger</h2>

        {/* WORKER FILTER */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Filter by Worker:</label>
          <select
            className="form-control"
            style={{ maxWidth: "250px", marginTop: "5px" }}
            value={selectedWorker}
            onChange={(e) => setSelectedWorker(e.target.value)}
          >
            <option value="">All Workers</option>
            {workers.map((w) => (
              <option key={w.id} value={w.id}>
                {w.worker_name}
              </option>
            ))}
          </select>
        </div>

        {/* ORIGINAL TABLE (UNTOUCHED) */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "10px",
            fontSize: "15px",
          }}
        >
          <thead>
            <tr style={{ background: "#d4af37", color: "white" }}>
              <th style={{ padding: "10px" }}>Date</th>
              <th style={{ padding: "10px" }}>Worker</th>
              <th style={{ padding: "10px" }}>Size</th>
              <th style={{ padding: "10px" }}>Color</th>
              <th style={{ padding: "10px" }}>Issued</th>
              <th style={{ padding: "10px" }}>Received</th>
            </tr>
          </thead>

          <tbody>
            {ledger.map((row) => (
              <tr key={row.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px" }}>
                  {row.created_at?.slice(0, 10)}
                </td>
                <td style={{ padding: "8px" }}>
                  {row.WORKERS?.worker_name}
                </td>
                <td style={{ padding: "8px" }}>{row.size}</td>
                <td style={{ padding: "8px" }}>{row.color}</td>
                <td style={{ padding: "8px" }}>
                  {row.type === "ISSUE" ? row.cts : "-"}
                </td>
                <td style={{ padding: "8px" }}>
                  {row.type === "RECEIVE" ? row.cts : "-"}
                </td>
              </tr>
            ))}

            {/* OLD TOTAL ROW */}
            <tr style={{ fontWeight: "bold", background: "#fff9d4" }}>
              <td style={{ padding: "10px" }} colSpan="4">
                TOTAL
              </td>
              <td style={{ padding: "10px" }}>{totalIssued}</td>
              <td style={{ padding: "10px" }}>{totalReceived}</td>
            </tr>
          </tbody>
        </table>

        {/* OLD BALANCE */}
        <h3 style={{ marginTop: "15px" }}>
          💠 Balance CTS: <b>{balance}</b>
        </h3>

        {/* NEW RECEIVE SUMMARY — BELOW OLD TABLE */}
        <h3 style={{ marginTop: "30px" }}>Received Stones (By Lot)</h3>

        {lotSummary.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No received stones for this worker.</div>
        ) : (
          lotSummary.map((row, i) => (
            <div key={i} className="lot-card" style={{ padding: 10 }}>
              <strong>{row.date}</strong> | <strong>{row.lot_no}</strong> | Big:{" "}
              <span style={{ color: "green" }}>{row.big}</span> | Micro:{" "}
              <span style={{ color: "blue" }}>{row.micro}</span>
            </div>
          ))
        )}

        {/* NEW TOTALS */}
        {lotSummary.length > 0 && (
          <div
            className="lot-card"
            style={{
              marginTop: 15,
              padding: 12,
              background: "#fff3d6",
              border: "1px solid #e0c48f",
              fontWeight: 700,
            }}
          >
            TOTAL BIG:{" "}
            <span style={{ color: "green" }}>
              {receiveTotals.big.toFixed(3)}
            </span>{" "}
            | TOTAL MICRO:{" "}
            <span style={{ color: "blue" }}>
              {receiveTotals.micro.toFixed(3)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiamondLedger;
